import {IconButton} from '@wandb/components';
import {SelectCreatable} from '@wandb/weave/components/Form/Select';
import React, {useState} from 'react';

import {internalTooltip, useIsInternalUser} from './internalTools';
import {useAgentAliases} from './useAgentAliases';

type AgentConfigOption = {value: string; label: string};

interface WBAgentAgentSelectorProps {
  value: string | null;
  onChange: (value: string | null) => void;
}

/**
 * WBAgentAgentSelector is an internal/dev/beta-only composer control that
 * overrides which named agent registry config the next turn runs on. It stays
 * collapsed as a subtle icon button (with an internal-only hover hint, like the
 * debug toggle) so it doesn't read as a customer feature; clicking it expands
 * the creatable dropdown — pick a suggestion or type a custom config name,
 * clearable to fall back to the service default. The value is owned by the
 * composer and sent with the next turn.
 */
export const WBAgentAgentSelector = ({
  value,
  onChange,
}: WBAgentAgentSelectorProps) => {
  const isInternal = useIsInternalUser();
  const [expanded, setExpanded] = useState(false);

  // Only internal users see the selector, so only they fetch the suggestions.
  const aliases = useAgentAliases(isInternal);

  // Hide the selector until the service reports at least one alias — there's
  // nothing to override to (and the fetch may still be in flight or have
  // failed), so an empty picker would be noise.
  if (!isInternal || aliases.length === 0) {
    return null;
  }

  const options: AgentConfigOption[] = aliases.map(alias => ({
    value: alias,
    label: alias,
  }));

  const selected: AgentConfigOption | null =
    value != null ? {value, label: value} : null;

  return (
    <div className="flex items-center gap-8">
      <IconButton
        variant="ghost"
        icon="robot-service-member"
        // Highlight when expanded or when an override is active, so a set
        // override is still discoverable while collapsed.
        isActive={expanded || value != null}
        aria-label="agent config override"
        onClick={() => setExpanded(prev => !prev)}
        tooltip={{children: internalTooltip('Pick agent')}}
      />
      {expanded && (
        <SelectCreatable<AgentConfigOption>
          className="w-[180px]"
          aria-label="agent config override"
          // Use medium so the selected value matches the menu/placeholder text
          // size (small renders the control text smaller than the options).
          size="medium"
          placeholder="Default agent"
          // Composer sits at the bottom of the chat, so open the menu upward to
          // keep it on screen. Focus + open on expand for one-click access.
          menuPlacement="top"
          // Portal the menu to the body so it escapes the composer's overflow
          // ancestors and the chat drawer's stacking context; the shared
          // menuPortal style (zIndex 2000) then paints it above the drawer.
          menuPortalTarget={document.body}
          menuPosition="fixed"
          autoFocus
          openMenuOnFocus
          isClearable
          options={options}
          value={selected}
          // Fires for selecting a suggestion, creating a typed value, and
          // clearing (null). Creatable surfaces typed text as the option value.
          onChange={option => {
            const next = option?.value?.trim();
            onChange(next ? next : null);
          }}
        />
      )}
    </div>
  );
};
